import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Bot, Check, Columns2, FilePlus2, Loader2, MessageSquarePlus, Send, Trash2, X } from "lucide-react";
import { api, API, errMsg, fmtDate, getAuthToken, getCompanyId } from "@/lib/api";
import { useFiles } from "@/context/FilesContext";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocPicker } from "@/components/ai/DocPicker";

const QUICK = [
  "Retrouve toutes les factures du fournisseur Michelin de 2026",
  "Résume les documents sélectionnés",
  "Extrais le numéro, la date et le montant de chaque facture",
  "Compare les documents sélectionnés et liste les différences",
  "Y a-t-il des factures en double ?",
  "Quels contrats arrivent à échéance prochainement ?",
];

function Message({ m, onOpen, label }) {
  const content = (m.content || "").replace(/\[(D\d+)\]/g, "[$1](#doc-$1)");
  const src = Object.fromEntries((m.sources || []).map((s) => [s.ref, s]));
  if (m.role === "user") {
    return <div className="flex justify-end"><div className="max-w-[85%] bg-[#1E3A8A] text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap" data-testid="chat-user-message">{m.content}</div></div>;
  }
  return (
    <div className="flex gap-3" data-testid="chat-assistant-message">
      <span className="h-8 w-8 rounded-lg bg-slate-900 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-amber-400" /></span>
      <div className="min-w-0 flex-1">
        {label && <div className="text-[11px] font-semibold text-slate-500 mb-1" data-testid="chat-message-model">{label}{m.kept_from_compare ? " · réponse retenue" : ""}</div>}
        <div className="prose-chat text-sm leading-relaxed text-slate-800 bg-white border rounded-2xl rounded-tl-sm px-4 py-3">
          {m.content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              a: ({ href, children }) => {
                if (href?.startsWith("#doc-")) {
                  const s = src[href.slice(5)];
                  return <button type="button" onClick={() => s && onOpen(s.id)} title={s?.name}
                    className="inline-flex items-center px-1.5 py-0 mx-0.5 rounded bg-blue-50 text-[#1E3A8A] text-xs font-semibold border border-blue-100 hover:bg-blue-100">{children}</button>;
                }
                return <a href={href} target="_blank" rel="noreferrer" className="text-[#1E3A8A] underline">{children}</a>;
              },
            }}>{content}</ReactMarkdown>
          ) : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
        {m.sources?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="chat-sources">
            {m.sources.map((s) => (
              <button key={s.ref} onClick={() => onOpen(s.id)} data-testid={`chat-source-${s.id}`}
                className="text-xs bg-white border rounded-full px-2.5 py-1 hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-colors max-w-[240px] truncate">
                <span className="font-semibold">{s.ref}</span> · {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

async function readSSE(res, onEvent) {
  const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n"); buf = parts.pop();
    for (const p of parts) if (p.startsWith("data: ")) onEvent(JSON.parse(p.slice(6)));
  }
}

function CompareMessage({ m, models, onOpen, onKeep }) {
  const label = (id) => models.find((x) => x.id === id)?.label || id;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="chat-compare-message">
      {m.models.map((id, i) => {
        const a = m.answers[id];
        return (
          <div key={id} className="bg-slate-100/70 border rounded-xl p-3 space-y-2 min-w-0" data-testid={`compare-col-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label(id)}</span>
              {a.done && a.candidate_id && (
                <Button size="sm" variant="outline" className="h-7 bg-white" onClick={() => onKeep(m, id)} data-testid={`compare-keep-${i}`}>
                  <Check className="h-3.5 w-3.5 mr-1" />Garder cette réponse
                </Button>
              )}
            </div>
            <Message m={{ role: "assistant", content: a.content, sources: a.done ? a.sources : [] }} onOpen={onOpen} />
            {a.error && <p className="text-xs text-red-600">{a.error}</p>}
          </div>
        );
      })}
    </div>
  );
}

export default function Assistant() {
  const [params, setParams] = useSearchParams();
  const { openFile } = useFiles();
  const { me } = useMe();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(() => localStorage.getItem("sdr-ai-model") || "claude-sonnet-5-5");
  const [convs, setConvs] = useState([]);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [focus, setFocus] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [compareModel, setCompareModel] = useState(null);
  const endRef = useRef(null);

  const loadConvs = () => api.get("/ai/conversations").then((r) => setConvs(r.data));
  useEffect(() => {
    api.get("/ai/models").then((r) => {
      setModels(r.data);
      setModel((r.data.find((m) => m.default) || r.data[0])?.id);
    });
    loadConvs();
  }, []);
  useEffect(() => { localStorage.setItem("sdr-ai-model", model); if (compareModel === model) setCompareModel(null); }, [model]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    const f = params.get("file"); const q = params.get("q");
    if (f) api.get(`/files/${f}`).then((r) => setFocus([{ id: r.data.id, name: r.data.name }])).catch(() => {});
    if (q) send(q);
    if (f || q) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openConv = async (id) => {
    const { data } = await api.get(`/ai/conversations/${id}`);
    setConvId(id); setMessages(data.messages); setFocus([]);
  };
  const newConv = () => { setConvId(null); setMessages([]); setFocus([]); };
  const delConv = async (id) => { await api.delete(`/ai/conversations/${id}`); if (id === convId) newConv(); loadConvs(); };

  const post = async (path, body) => {
    const call = async (fresh) => fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getAuthToken(fresh ? { skipCache: true } : undefined)}`, ...(getCompanyId() ? { "X-Company-Id": getCompanyId() } : {}) },
      body: JSON.stringify(body),
    });
    let res = await call(false);
    if (res.status === 401) res = await call(true);
    if (!res.ok) {
      const d = (await res.json().catch(() => ({}))).detail;
      throw new Error(res.status === 401 ? "Votre session a expiré, merci de vous reconnecter." : d || "Erreur");
    }
    return res;
  };
  const onMeta = (ev) => { convIdRef.current = ev.conversation_id; setConvId(ev.conversation_id); };

  async function send(text) {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput(""); setBusy(true);
    const base = { message: msg, conversation_id: convIdRef.current, file_ids: focus.map((f) => f.id) };
    const patchLast = (fn) => setMessages((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      if (compareModel && compareModel !== model) {
        const pair = [model, compareModel];
        const empty = Object.fromEntries(pair.map((id) => [id, { content: "", sources: [], done: false }]));
        setMessages((m) => [...m, { role: "user", content: msg }, { role: "compare", models: pair, answers: empty }]);
        const upd = (id, fn) => patchLast((l) => ({ ...l, answers: { ...l.answers, [id]: fn(l.answers[id]) } }));
        const res = await post("/ai/compare", { ...base, models: pair });
        await readSSE(res, (ev) => {
          if (ev.type === "meta") onMeta(ev);
          if (ev.type === "delta") upd(ev.model, (a) => ({ ...a, content: a.content + ev.content }));
          if (ev.type === "done") upd(ev.model, (a) => ({ ...a, done: true, sources: ev.sources, candidate_id: ev.candidate_id }));
          if (ev.type === "error") upd(ev.model, (a) => ({ ...a, error: ev.message }));
        });
      } else {
        setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", sources: [] }]);
        const res = await post("/ai/chat", { ...base, model });
        await readSSE(res, (ev) => {
          if (ev.type === "meta") onMeta(ev);
          if (ev.type === "delta") patchLast((l) => ({ ...l, content: l.content + ev.content }));
          if (ev.type === "done") patchLast((l) => ({ ...l, sources: ev.sources }));
          if (ev.type === "error") toast.error(ev.message);
        });
      }
      loadConvs();
    } catch (e) {
      toast.error(e.message || errMsg(e));
      patchLast((l) => (l.role === "compare" ? l : { ...l, content: l.content || "_Réponse indisponible._" }));
    } finally { setBusy(false); }
  }

  const keep = async (cm, id) => {
    try {
      const { data } = await api.post("/ai/compare/keep", { candidate_id: cm.answers[id].candidate_id });
      setMessages((ms) => ms.map((x) => (x === cm ? { role: "assistant", content: data.content, sources: data.sources, model: data.model, kept_from_compare: true } : x)));
      toast.success("Réponse enregistrée dans la conversation");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const convIdRef = useRef(null);
  useEffect(() => { convIdRef.current = convId; }, [convId]);

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)] lg:h-[calc(100vh-8rem)] flex gap-6 fade-up" data-testid="assistant-page">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-white border rounded-xl overflow-hidden">
        <div className="p-3 border-b"><Button className="w-full" onClick={newConv} data-testid="chat-new-btn"><MessageSquarePlus className="h-4 w-4 mr-2" />Nouvelle conversation</Button></div>
        <div className="flex-1 overflow-auto p-2 space-y-0.5" data-testid="chat-conversations">
          {convs.length === 0 && <p className="text-xs text-slate-500 p-3">Aucune conversation</p>}
          {convs.map((c) => (
            <div key={c.id} className={`group flex items-center rounded-md ${c.id === convId ? "bg-blue-50" : "hover:bg-slate-50"}`}>
              <button onClick={() => openConv(c.id)} className="flex-1 min-w-0 text-left px-3 py-2" data-testid={`chat-conv-${c.id}`}>
                <div className="text-sm truncate">{c.title}</div><div className="text-[11px] text-slate-500">{fmtDate(c.updated_at, true)}</div>
              </button>
              <button onClick={() => delConv(c.id)} className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-red-600" aria-label="Supprimer" data-testid={`chat-conv-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </aside>

      <section className="flex-1 min-w-0 flex flex-col bg-slate-50">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Assistant IA</h1>
            <p className="text-xs text-slate-500">Répond uniquement à partir des documents auxquels vous avez accès.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="md:hidden" onClick={newConv} aria-label="Nouvelle conversation"><MessageSquarePlus className="h-4 w-4" /></Button>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="w-36 sm:w-44 bg-white" data-testid="chat-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>{models.map((m) => <SelectItem key={m.id} value={m.id} title={m.description}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            {compareModel ? (
              <>
                <span className="text-xs font-semibold text-slate-500">vs</span>
                <Select value={compareModel} onValueChange={setCompareModel}>
                  <SelectTrigger className="w-36 sm:w-44 bg-white" data-testid="chat-compare-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{models.filter((m) => m.id !== model).map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={() => setCompareModel(null)} aria-label="Arrêter la comparaison" data-testid="chat-compare-off"><X className="h-4 w-4" /></Button>
              </>
            ) : (
              <Button variant="outline" size="sm" className="bg-white" data-testid="chat-compare-on"
                onClick={() => setCompareModel(models.find((m) => m.id !== model)?.id || null)}>
                <Columns2 className="h-4 w-4 mr-1.5" />Comparer
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto space-y-5 pr-1" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="py-8">
              <div className="tread-bg rounded-2xl p-6 text-white">
                <Bot className="h-8 w-8 text-amber-400" />
                <h2 className="font-display text-xl font-bold mt-3">Bonjour {me.name?.split(" ")[0]}, que cherchez-vous ?</h2>
                <p className="text-sm text-slate-300 mt-1">Retrouver une facture, résumer un contrat, extraire des montants, comparer des devis…</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {QUICK.map((q, i) => (
                  <button key={q} onClick={() => send(q)} data-testid={`chat-quick-${i}`}
                    className="text-left text-sm bg-white border rounded-lg px-4 py-3 hover:border-[#1E3A8A] hover:-translate-y-0.5 transition-[transform,border-color]">{q}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (m.role === "compare"
            ? <CompareMessage key={i} m={m} models={models} onOpen={openFile} onKeep={keep} />
            : <Message key={i} m={m} onOpen={openFile} label={m.model ? models.find((x) => x.id === m.model)?.label : null} />))}
          <div ref={endRef} />
        </div>

        <div className="mt-4 bg-white border rounded-xl p-3">
          {focus.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chat-focus-files">
              {focus.map((f) => (
                <span key={f.id} className="text-xs bg-blue-50 text-[#1E3A8A] border border-blue-100 rounded-full pl-2.5 pr-1 py-0.5 flex items-center gap-1 max-w-[240px]">
                  <span className="truncate">{f.name}</span>
                  <button onClick={() => setFocus((x) => x.filter((y) => y.id !== f.id))} aria-label="Retirer"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <Button variant="ghost" size="icon" onClick={() => setPicker(true)} aria-label="Joindre des documents" data-testid="chat-attach-btn"><FilePlus2 className="h-5 w-5" /></Button>
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} placeholder="Posez votre question sur vos documents…"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              className="min-h-[44px] max-h-40 resize-none border-0 focus-visible:ring-0 shadow-none" data-testid="chat-input" />
            <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="chat-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </section>
      <DocPicker open={picker} onOpenChange={setPicker} selected={focus} onChange={setFocus} />
    </div>
  );
}
